import buildType20pxIcon from '@jetbrains/icons/buildType-20px.svg?react'
import buildTypeComposite20pxIcon from '@jetbrains/icons/buildType-composite-20px.svg?react'
import buildTypeCompositeEdit20pxIcon from '@jetbrains/icons/buildType-composite-edit-20px.svg?react'
import buildTypeCompositeError20pxIcon from '@jetbrains/icons/buildType-composite-error-20px.svg?react'
import buildTypeCompositeErrorIcon from '@jetbrains/icons/buildType-composite-error.svg?react'
import buildTypeCompositeIcon from '@jetbrains/icons/buildType-composite.svg?react'
import buildTypeEdit20pxIcon from '@jetbrains/icons/buildType-edit-20px.svg?react'
import buildTypeError20pxIcon from '@jetbrains/icons/buildType-error-20px.svg?react'
import buildTypeErrorIcon from '@jetbrains/icons/buildType-error.svg?react'
import buildTypeIcon from '@jetbrains/icons/buildType.svg?react'
import pipelineIcon20pxIcon from '@jetbrains/icons/pipeline-20px.svg?react'
import pipelineIconEdit20pxIcon from '@jetbrains/icons/pipeline-edit-20px.svg?react'
import pipelineIcon from '@jetbrains/icons/pipeline.svg?react'
import project20pxIcon from '@jetbrains/icons/project-20px.svg?react'
import projectEdit20pxIcon from '@jetbrains/icons/project-edit-20px.svg?react'
import projectError20pxIcon from '@jetbrains/icons/project-error-20px.svg?react'
import projectErrorIcon from '@jetbrains/icons/project-error.svg?react'
import projectIcon from '@jetbrains/icons/project.svg?react'
import template20pxIcon from '@jetbrains/icons/template-20px.svg?react'
import templateEdit20pxIcon from '@jetbrains/icons/template-edit-20px.svg?react'
import templateIcon from '@jetbrains/icons/template.svg?react'
import type {IconType as Icon} from '@jetbrains/ring-ui-built/components/icon/icon'
import {SvgIcon} from '@jetbrains/teamcity-ui/dist/components'
import type {KeyValue} from '@jetbrains/teamcity-ui/dist/utils'
import classNames from 'classnames'
import * as React from 'react'

import {BuildStatusType} from '../../../types'

import type {DefaultProps, Props} from './ProjectOrBuildTypeIcon.types'
import {EntityIconType, Sizes} from './ProjectOrBuildTypeIcon.types'

import styles from './ProjectOrBuildTypeIcon.module.css'

const Icons: KeyValue<string, Icon> = {
  'buildType-error': buildTypeErrorIcon,
  buildType: buildTypeIcon,
  'buildType-error-20px': buildTypeError20pxIcon,
  'buildType-20px': buildType20pxIcon,
  'buildType-edit-20px': buildTypeEdit20pxIcon,
  'buildType-composite-error': buildTypeCompositeErrorIcon,
  'buildType-composite': buildTypeCompositeIcon,
  'buildType-composite-error-20px': buildTypeCompositeError20pxIcon,
  'buildType-composite-20px': buildTypeComposite20pxIcon,
  'buildType-composite-edit-20px': buildTypeCompositeEdit20pxIcon,
  'project-error': projectErrorIcon,
  project: projectIcon,
  'project-error-20px': projectError20pxIcon,
  'project-20px': project20pxIcon,
  'project-edit-20px': projectEdit20pxIcon,
  template: templateIcon,
  'template-20px': template20pxIcon,
  'template-edit-20px': templateEdit20pxIcon,
  pipeline: pipelineIcon,
  'pipeline-20px': pipelineIcon20pxIcon,
  'pipeline-edit-20px': pipelineIconEdit20pxIcon,
}

const getIcon = (name: string) => Icons[name] ?? name

const primaryTypes = new Set<EntityIconType>([
  EntityIconType.PROJECT,
  EntityIconType.BUILD_TYPE,
  EntityIconType.PIPELINE,
])

class ProjectOrBuildTypeIcon extends React.PureComponent<Props> {
  static defaultProps: DefaultProps = {
    type: EntityIconType.PROJECT,
    size: 'M',
  }

  render(): React.ReactNode {
    const {
      type,
      composite,
      status,
      size,
      className,
      title,
      paused,
      edit,
      locked,
      running,
      failedToStart,
    } = this.props

    if (type == null) {
      return null
    }

    const failed = status === BuildStatusType.FAILURE || status === 'failed'
    const canceled = status === BuildStatusType.CANCELED
    const queued = status === BuildStatusType.QUEUED

    const typeIsInvestigation = type === EntityIconType.INVESTIGATION
    const typeIsPrimary = primaryTypes.has(type)
    const typeIsRunnable = type === EntityIconType.BUILD_TYPE || type === EntityIconType.PIPELINE
    const showPaused = paused && !typeIsInvestigation && !locked

    const classes = classNames(
      styles.icon,
      className,
      (typeIsPrimary || typeIsInvestigation) && status && styles[status],
      {
        [styles.iconL]: size === 'L',
        [styles.paused]: showPaused,
        [styles.edit]: edit,
        [styles.locked]: locked,
        [styles.running]: running,
      },
    )
    const name = [
      type,
      type === 'buildType' && composite === true && 'composite',
      typeIsRunnable && running && 'spinner',
      typeIsPrimary && !running && failed && !failedToStart && 'error',
      typeIsPrimary && !running && failed && failedToStart && 'warning',
      typeIsRunnable && !running && canceled && 'cancel',
      typeIsRunnable && !running && queued && 'hourglass',
      showPaused && 'paused',
      edit && 'edit',
      locked && 'locked',
      size === 'M' ? null : `${Sizes[size]}px`,
    ]
      .filter(Boolean)
      .join('-')
    return <SvgIcon className={classes} icon={getIcon(name)} title={title} />
  }
}

export default ProjectOrBuildTypeIcon
